import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ButtonProps,
} from '@mui/material';
import {
  Delete,
  Security,
  Group,
  Block,
  PersonRemove,
  ErrorOutline,
} from '@mui/icons-material';
import {
  UserAdministrationDto,
  BulkUserOperationType,
  TeamDto,
  BulkUserOperationData,
} from '@act/shared/data-transfer-objects';
import { BUTTON_COLORS, reemaColors } from 'toolkit/styles/colors';
import { userAppSelectors } from 'redux/slices/usersSlice';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { UserRole } from '@act/shared/definitions';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';

interface BulkOperationConfig {
  type: BulkUserOperationType;
  label: string;
  icon: React.ReactNode;
  color?: BUTTON_COLORS;
  variant: ButtonProps['variant'];
  data?: BulkUserOperationData;
  confirmationTitle?: string;
  confirmationMessage?: (count: number, data?: BulkUserOperationData) => string;
  confirmationIcon?: React.ReactElement;
  confirmationColor?: BUTTON_COLORS;
  additionalInputComponent?: (
    data: BulkUserOperationData,
    setData: (data: BulkUserOperationData) => void
  ) => React.ReactNode;
  superAdminOnly?: boolean;
}

const getBulkOperations = (teams: TeamDto[]): BulkOperationConfig[] => [
  {
    type: BulkUserOperationType.TOGGLE_APP_ACCESS,
    label: 'Enable App Access',
    icon: <Security />,
    variant: 'outlined',
    data: { hasAppAccess: true },
    confirmationTitle: 'Enable App Access',
    confirmationMessage: count =>
      `Are you sure you want to enable app access for ${count} user(s)?`,
    confirmationIcon: <Security />,
    superAdminOnly: true,
  },
  {
    type: BulkUserOperationType.TOGGLE_APP_ACCESS,
    label: 'Disable App Access',
    icon: <Block />,
    variant: 'outlined',
    data: { hasAppAccess: false },
    confirmationTitle: 'Disable App Access',
    confirmationMessage: count =>
      `Are you sure you want to disable app access for ${count} user(s)?`,
    confirmationIcon: <Block />,
    superAdminOnly: true,
  },
  {
    type: BulkUserOperationType.ASSIGN_TEAMS,
    label: 'Add Teams',
    icon: <Group />,
    variant: 'outlined',
    confirmationTitle: 'Add Teams',
    confirmationMessage: (count, data) =>
      `Are you sure you want to add ${
        data?.teamIds?.length || 0
      } team(s) to ${count} user(s)? They will be added as "viewer" by default.`,
    confirmationIcon: <Group />,
    additionalInputComponent: (data, setData) => (
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>Select Teams to Add</InputLabel>
        <Select
          multiple
          value={data?.teamIds || []}
          onChange={e => setData({ teamIds: e.target.value as number[] })}
          label="Select Teams to Add"
        >
          {teams.map(team => (
            <MenuItem key={team.id} value={team.id}>
              {team.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ),
  },
  {
    type: BulkUserOperationType.REMOVE_TEAMS,
    label: 'Remove Teams',
    icon: <PersonRemove />,
    variant: 'outlined',
    confirmationTitle: 'Remove Teams',
    confirmationMessage: (count, data) =>
      `Are you sure you want to remove ${
        data?.teamIds?.length || 0
      } team(s) from ${count} user(s)?`,
    confirmationIcon: <PersonRemove />,
    confirmationColor: 'warning',
    additionalInputComponent: (data, setData) => (
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>Select Teams to Remove</InputLabel>
        <Select
          multiple
          value={data?.teamIds || []}
          onChange={e => setData({ teamIds: e.target.value as number[] })}
          label="Select Teams to Remove"
        >
          {teams.map(team => (
            <MenuItem key={team.id} value={team.id}>
              {team.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    ),
  },
  {
    type: BulkUserOperationType.DELETE_USERS,
    label: 'Delete Users',
    icon: <Delete />,
    color: 'error',
    variant: 'outlined',
    confirmationTitle: 'Delete Users',
    confirmationMessage: count =>
      `Are you sure you want to delete ${count} user(s)? This action cannot be undone.`,
    confirmationIcon: <ErrorOutline />,
    confirmationColor: 'error',
    superAdminOnly: true,
  },
];

interface BulkUserOperationsToolbarProps {
  selectedUsers: UserAdministrationDto[];
  onBulkOperation: (
    operation: BulkUserOperationType,
    data?: BulkUserOperationData
  ) => void;
  isLoading?: boolean;
  onClearSelection: () => void;
}

export const BulkUserOperationsToolbar: React.FC<
  BulkUserOperationsToolbarProps
> = ({
  selectedUsers,
  onBulkOperation,
  isLoading = false,
  onClearSelection,
}) => {
  const teams = userAppSelectors.useSelectAllTeams();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const openConfirmationAlertDialog = useConfirmationAlertDialog();

  // State for operations requiring additional input
  const [additionalInputDialogOpen, setAdditionalInputDialogOpen] =
    useState(false);
  const [selectedOperationConfig, setSelectedOperationConfig] =
    useState<BulkOperationConfig | null>(null);
  const [operationData, setOperationData] =
    useState<BulkUserOperationData | null>(null);

  const selectedCount = selectedUsers.length;

  const handleOperationClick = async (config: BulkOperationConfig) => {
    setSelectedOperationConfig(config);
    setOperationData(config.data || null);

    // Always show confirmation dialog - simplified single flow
    if (config.additionalInputComponent) {
      // Show custom dialog for operations requiring additional input
      setAdditionalInputDialogOpen(true);
    } else {
      // Use ConfirmationAlertDialog for simple confirmations
      const confirmed = await openConfirmationAlertDialog({
        title: config.confirmationTitle || 'Confirm Operation',
        message:
          config.confirmationMessage?.(selectedCount, config.data) ||
          `Are you sure you want to perform this operation on ${selectedCount} user(s)?`,
        icon: config.confirmationIcon,
        iconColor: config.color || 'primary',
        confirmButtonColor: config.color || 'primary',
        confirmationText: 'Confirm',
        cancelText: 'Cancel',
      });

      if (confirmed) {
        onBulkOperation(config.type, config.data);
      }
    }
  };

  const handleConfirmOperation = () => {
    if (selectedOperationConfig) {
      onBulkOperation(selectedOperationConfig.type, operationData);
      setAdditionalInputDialogOpen(false);
      setSelectedOperationConfig(null);
      setOperationData(null);
    }
  };

  const handleCancelOperation = () => {
    setAdditionalInputDialogOpen(false);
    setSelectedOperationConfig(null);
    setOperationData(null);
  };

  const getSelectedUserNames = (): React.ReactNode => {
    return (
      <ul>
        {selectedUsers.map(user => (
          <li key={user.id}>
            {user.firstName} {user.lastName}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          backgroundColor: reemaColors.lightBackground,
          border: `1px solid ${reemaColors.borderCard}`,
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {selectedCount} user{selectedCount === 1 ? '' : 's'} selected
          </Typography>
          <Button
            variant="text"
            onClick={onClearSelection}
            disabled={isLoading}
            size="small"
          >
            Clear Selection
          </Button>
        </Box>
        {selectedCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              width: 'auto',
            }}
          >
            {getBulkOperations(teams)
              .filter(config =>
                config.superAdminOnly
                  ? actingUser?.role === UserRole.SUPER_ADMIN
                  : true
              )
              .map((config, index) => (
                <Button
                  key={`${config.type}-${index}`}
                  variant="outlined"
                  color={config.color || 'primary'}
                  startIcon={config.icon}
                  onClick={() => handleOperationClick(config)}
                  disabled={isLoading}
                  size="small"
                >
                  {config.label}
                </Button>
              ))}
          </Box>
        )}
      </Box>

      {/* Custom Dialog for operations requiring additional input */}
      <Dialog
        open={additionalInputDialogOpen}
        onClose={handleCancelOperation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedOperationConfig?.confirmationTitle || 'Confirm Operation'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {selectedOperationConfig?.confirmationMessage?.(
              selectedCount,
              operationData
            ) ||
              `Are you sure you want to perform this operation on ${selectedCount} user(s)?`}
          </DialogContentText>

          {selectedOperationConfig?.additionalInputComponent &&
            selectedOperationConfig.additionalInputComponent(
              operationData,
              setOperationData
            )}

          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            Selected users: {getSelectedUserNames()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOperation} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmOperation}
            variant="contained"
            color={
              selectedOperationConfig?.confirmationColor === 'error'
                ? 'error'
                : selectedOperationConfig?.confirmationColor === 'warning'
                ? 'warning'
                : selectedOperationConfig?.color === 'success'
                ? 'success'
                : 'primary'
            }
            disabled={
              isLoading ||
              (selectedOperationConfig?.additionalInputComponent &&
                (selectedOperationConfig?.type ===
                  BulkUserOperationType.ASSIGN_TEAMS ||
                  selectedOperationConfig?.type ===
                    BulkUserOperationType.REMOVE_TEAMS) &&
                (!operationData?.teamIds || operationData.teamIds.length === 0))
            }
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
